import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { FontLoader } from 'three/addons/loaders/FontLoader.js'
import { gsap } from "gsap"

import GoldSweep from '../particles/GoldSweep'
import PerfumeBottle from '../threeMeshes/bottlemesh'

export default class ThreeController {

    constructor() {

        this.clock = new THREE.Clock()
        this.threeElements = []

        // ─────────────────────────────────────────────────────
        // SESSION-BASED LOADER LOGIC
        // ─────────────────────────────────────────────────────

        // First visit in this browser tab/session?
        this.hasVisited = sessionStorage.getItem('siteLoaded')

        // First visit = cinematic
        // Returning visit = fast
        this.minimumLoadingTime = this.hasVisited ? 350 : 1800

        // Loader state
        this.actualProgress = 0
        this.displayedProgress = 0
        this.loadingComplete = false
        this.loadingStartTime = performance.now()

        this.init()
    }

    init() {
        this._load()
        this._setupScene()

        this.perfumeBottle = new PerfumeBottle(this.scene)
    }

    // ─────────────────────────────────────────────────────
    // PARTICLES
    // ─────────────────────────────────────────────────────
    createParticles() {
        this.particles = new GoldSweep()
    }

    smokeAnimation() {
        if (this.particles) {
            this.particles.smokeAnimation()
        }
    }

    // ─────────────────────────────────────────────────────
    // PERFUME BOTTLE
    // ─────────────────────────────────────────────────────
    async addPerfumeBottle(perfumeImage, perfumeName) {

        try {
            await Promise.race([
                document.fonts.load('10pt "Tangerine"'),
                new Promise(resolve => setTimeout(resolve, 1000))
            ])
        } catch(e) {}

        if (!this.perfumeBottle) {
            this.perfumeBottle = new PerfumeBottle(this.scene)
        }

        this.perfumeBottle.addBottle(perfumeImage, perfumeName)
    }

    removeCurrentPerfumeBottle() {
        if (this.perfumeBottle) {
            this.perfumeBottle.removeBottle()
        }
    }

    // ─────────────────────────────────────────────────────
    // LOADING MANAGER
    // ─────────────────────────────────────────────────────
    _load() {

        this.loadingManager = new THREE.LoadingManager()

        this.loadingManager.onProgress = (url, loaded, total) => {

            const threeProgress = total > 0
                ? (loaded / total) * 50
                : 0

            this._setActualProgress(
                Math.max(this.actualProgress, threeProgress)
            )
        }

        this.loadingManager.onLoad = () => {
            this._setActualProgress(100)
        }
    }

    // ─────────────────────────────────────────────────────
    // SMOOTH LOADER
    // ─────────────────────────────────────────────────────
    _setActualProgress(value) {
        this.actualProgress = Math.min(100, value)
    }

    _animateLoadingBar() {

        const animate = () => {

            this.displayedProgress +=
                (this.actualProgress - this.displayedProgress) * 0.06

            const rounded = Math.round(this.displayedProgress)

            const bar = document.getElementById('loading-bar')
            const pct = document.getElementById('loading-percentage')

            if (bar) {
                bar.style.width = rounded + '%'
            }

            if (pct) {
                pct.textContent = rounded + '%'
            }

            if (!this.loadingComplete || this.displayedProgress < 99.5) {
                requestAnimationFrame(animate)
            }
        }

        animate()
    }

    // ─────────────────────────────────────────────────────
    // ASSET TRACKING
    // ─────────────────────────────────────────────────────
    _trackPageAssets() {

        const self = this

        this._animateLoadingBar()

        // ─────────────────────────────────────────────────
        // IMAGES
        // ─────────────────────────────────────────────────
        const allImages = Array.from(document.images)

        // Ignore loading screen images/canvas
        const validImages = allImages.filter(img => {
            return !img.closest('#loading-screen')
        })

        const imgTotal = validImages.length

        let imgLoaded = validImages.filter(img => {
            return img.complete && img.naturalWidth > 0
        }).length

        // ─────────────────────────────────────────────────
        // FONTS
        // ─────────────────────────────────────────────────
        let fontsReady = false

        Promise.race([
            document.fonts.ready,
            new Promise(resolve => setTimeout(resolve, 1200))
        ]).then(() => {

            fontsReady = true

            updateProgress()
            checkDone()
        })

        // ─────────────────────────────────────────────────
        // PROGRESS CALCULATION
        // ─────────────────────────────────────────────────
        function calcPct() {

            const imgPct = imgTotal > 0
                ? (imgLoaded / imgTotal) * 85
                : 85

            const fontPct = fontsReady ? 15 : 0

            return Math.min(99, imgPct + fontPct)
        }

        function updateProgress() {
            self._setActualProgress(calcPct())
        }

        function checkDone() {

            const allImgsDone =
                imgLoaded >= imgTotal || imgTotal === 0

            if (allImgsDone && fontsReady) {

                self._setActualProgress(100)

                const elapsed =
                    performance.now() - self.loadingStartTime

                const remaining =
                    Math.max(0, self.minimumLoadingTime - elapsed)

                setTimeout(() => {
                    self._completeLoadingScreen()
                }, remaining)
            }
        }

        // ─────────────────────────────────────────────────
        // NO IMAGES
        // ─────────────────────────────────────────────────
        if (imgTotal === 0) {

            updateProgress()

            setTimeout(() => {

                if (!self.loadingComplete) {
                    self._completeLoadingScreen()
                }

            }, 4000)

            return
        }

        // ─────────────────────────────────────────────────
        // TRACK IMAGE LOADS
        // ─────────────────────────────────────────────────
        validImages.forEach(img => {

            if (!img.complete || img.naturalWidth === 0) {

                img.addEventListener('load', () => {

                    imgLoaded++
                    updateProgress()
                    checkDone()

                }, { once: true })

                img.addEventListener('error', () => {

                    imgLoaded++
                    updateProgress()
                    checkDone()

                }, { once: true })
            }
        })

        // Initial update
        updateProgress()

        // Safety fallback
        setTimeout(() => {

            if (!self.loadingComplete) {

                self._setActualProgress(100)

                setTimeout(() => {
                    self._completeLoadingScreen()
                }, 500)
            }

        }, 7000)
    }

    // ─────────────────────────────────────────────────────
    // COMPLETE LOADER
    // ─────────────────────────────────────────────────────
    _completeLoadingScreen() {

        if (this.loadingComplete) return

        this.loadingComplete = true

        // IMPORTANT:
        // remember user visited during this session
        sessionStorage.setItem('siteLoaded', 'true')

        const screen = document.getElementById('loading-screen')

        if (!screen || screen.style.display === 'none') {
            return
        }

        // Allow clicks immediately
        screen.style.pointerEvents = 'none'

        gsap.to(screen, {
            opacity: 0,
            duration: 0.85,
            ease: 'power2.inOut',
            onComplete: () => {
                screen.style.display = 'none'
            }
        })
    }

    // ─────────────────────────────────────────────────────
    // SCENE SETUP
    // ─────────────────────────────────────────────────────
    _setupScene() {

        const sizes = {
            width: window.innerWidth,
            height: window.innerHeight
        }

        this.canvas = document.querySelector('canvas.webgl')

        this.scene = new THREE.Scene()

        // IMPORTANT:
        // use loading manager
        this.textureLoader = new THREE.TextureLoader(this.loadingManager)

        this.fontLoader = new FontLoader(this.loadingManager)

        this.textureLoader.crossOrigin = ''

        this.renderer = new THREE.WebGLRenderer({
            canvas: this.canvas,
            antialias: true,
            powerPreference: 'high-performance'
        })

        this.camera = new THREE.PerspectiveCamera(
            75,
            sizes.width / sizes.height,
            0.1,
            100
        )

        this.scene.add(this.camera)

        this._configureCamera()
        this._configureRenderer(sizes)
        this._setControls()
        this._setupLights()

        // Start render loop
        this.tick()

        // Start loader tracking
        if (this.hasVisited) {
                
            this._setActualProgress(100)
                
            setTimeout(() => {
                this._completeLoadingScreen()
            }, 250)
        
        } else {
        
            this._trackPageAssets()
        }
    }

    // ─────────────────────────────────────────────────────
    // LIGHTS
    // ─────────────────────────────────────────────────────
    _setupLights() {

        const dirLight = new THREE.DirectionalLight('#ffffff', 3)

        dirLight.castShadow = true
        dirLight.shadow.camera.far = 15

        // Better performance
        dirLight.shadow.mapSize.set(512, 512)

        dirLight.shadow.normalBias = 0.05

        dirLight.position.set(0.25, 3, -2.25)

        this.scene.add(dirLight)

        const pointLight = new THREE.PointLight('#f5c42b', 3)

        // Massive performance win
        pointLight.castShadow = false

        pointLight.position.set(0.25, 3, -2.25)

        this.scene.add(pointLight)
    }

    // ─────────────────────────────────────────────────────
    // CONTROLS
    // ─────────────────────────────────────────────────────
    _setControls() {

        const controls = new OrbitControls(
            this.camera,
            this.canvas
        )

        controls.enableDamping = true
    }

    // ─────────────────────────────────────────────────────
    // RENDERER
    // ─────────────────────────────────────────────────────
    _configureRenderer(sizes) {

        this.renderer.setClearColor(0x000000, 0)

        this.renderer.toneMapping =
            THREE.ACESFilmicToneMapping

        this.renderer.toneMappingExposure = 0.55

        this.renderer.shadowMap.enabled = true

        this.renderer.shadowMap.type =
            THREE.PCFSoftShadowMap

        this.renderer.setSize(
            sizes.width,
            sizes.height
        )

        // HUGE optimization for Retina/4K
        this.renderer.setPixelRatio(
            Math.min(window.devicePixelRatio, 1.5)
        )
    }

    // ─────────────────────────────────────────────────────
    // CAMERA
    // ─────────────────────────────────────────────────────
    _configureCamera() {

        this.camera.position.set(0, 2, 5)

        this.camera.near = 0.1
        this.camera.far = 100

        this.camera.updateProjectionMatrix()
    }

    // ─────────────────────────────────────────────────────
    // TICK
    // ─────────────────────────────────────────────────────
    tickElements(time) {

        this.threeElements.forEach(el => {
            el.tick(time)
        })
    }

    tick() {

        const time = Date.now() * 0.0005

        this.tickElements(time)

        if (this.perfumeBottle) {
            this.perfumeBottle.tick(time)
        }

        this.renderer.render(
            this.scene,
            this.camera
        )

        requestAnimationFrame(
            this.tick.bind(this)
        )
    }
}